import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Percent,
  DollarSign,
  Gift,
  Sparkles,
  Copy,
  LockOpen,
  Tag,
  Truck,
  Star,
  Loader2,
  ExternalLink,
  CheckCircle,
  Clock,
  Users,
  Search,
  Ticket
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dealIcons = {
  percentage_off: Percent,
  fixed_amount: DollarSign,
  free_item: Gift,
  free_service: Sparkles,
  bogo: Copy,
  exclusive_access: LockOpen,
  member_pricing: Tag,
  free_shipping: Truck,
  other: Star
};

const dealColors = {
  percentage_off: 'bg-green-100 text-green-600',
  fixed_amount: 'bg-blue-100 text-blue-600',
  free_item: 'bg-purple-100 text-purple-600',
  free_service: 'bg-pink-100 text-pink-600',
  bogo: 'bg-orange-100 text-orange-600',
  exclusive_access: 'bg-indigo-100 text-indigo-600',
  member_pricing: 'bg-teal-100 text-teal-600',
  free_shipping: 'bg-amber-100 text-amber-600',
  other: 'bg-gray-100 text-gray-600'
};

const PartnerDeals = () => {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [dealTypes, setDealTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterPartner, setFilterPartner] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [typesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/partnerships/deal-types`),
        axios.get(`${API}/partnerships/summary`)
      ]);
      setDealTypes(typesRes.data);
      setSummary(summaryRes.data);

      if (user) {
        const [dealsRes, partnershipsRes] = await Promise.all([
          axios.get(`${API}/partnerships/deals`),
          axios.get(`${API}/partnerships`)
        ]);
        setDeals(dealsRes.data);
        setPartnerships(partnershipsRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (deal) => {
    if (!deal.can_redeem) {
      toast.error('You cannot redeem this deal');
      return;
    }

    setRedeeming(true);
    try {
      const response = await axios.post(`${API}/partnerships/deals/${deal.id}/redeem`, {
        deal_id: deal.id
      });
      toast.success('Deal redeemed successfully!');
      
      // Show promo code if available
      if (response.data.promo_code) {
        toast.info(`Your promo code: ${response.data.promo_code}`, {
          duration: 10000
        });
      }
      
      fetchData();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem deal');
    } finally {
      setRedeeming(false);
    }
  };

  const filteredDeals = deals.filter(deal => {
    if (filterType !== 'all' && deal.deal_type !== filterType) return false;
    if (filterPartner !== 'all' && deal.partnership_id !== filterPartner) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        deal.title.toLowerCase().includes(query) ||
        deal.description.toLowerCase().includes(query) ||
        deal.partner_name.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const DealIcon = ({ type }) => {
    const Icon = dealIcons[type] || Star;
    return <Icon className="w-5 h-5" />;
  };

  const formatDiscount = (deal) => {
    if (!deal.discount_value) return '';
    if (deal.deal_type === 'percentage_off') return `${deal.discount_value}% OFF`;
    if (deal.deal_type === 'fixed_amount') return `$${deal.discount_value} OFF`;
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-lg mx-auto text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-6 flex items-center justify-center">
                <Ticket className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-3xl font-heading font-medium mb-4">Partner Deals</h1>
              <p className="text-muted-foreground mb-6">
                AADC partners with pet businesses to bring you exclusive discounts and offers. 
                Register for free to access these member-only deals!
              </p>
              {summary && (
                <div className="flex justify-center gap-8 mb-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary">{summary.active_partnerships}</p>
                    <p className="text-sm text-muted-foreground">Partners</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary">{summary.active_deals}</p>
                    <p className="text-sm text-muted-foreground">Active Deals</p>
                  </div>
                </div>
              )}
              <Button 
                className="btn-primary"
                onClick={() => window.location.href = '/register'}
                data-testid="register-for-deals"
              >
                Register to View Deals
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Beta Testing Disclaimer */}
        <div className="mb-6 p-4 bg-gray-100 border border-gray-300 rounded-lg">
          <div className="flex items-center gap-2 text-gray-700">
            <span className="text-lg">⚠️</span>
            <p className="font-medium">Beta Testing Only</p>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            All deals shown on this page are sample/test data for beta testing purposes. 
            These offers are <strong>not valid</strong> and cannot be redeemed in real life. 
            Real partner deals will be added soon!
          </p>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
            <Ticket className="w-8 h-8 text-primary" />
            Partner Deals
          </h1>
          <p className="text-muted-foreground">
            Exclusive discounts and offers from our partner businesses
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Partners</p>
                <p className="font-medium text-lg">{partnerships.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Tag className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Deals</p>
                <p className="font-medium text-lg">{deals.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Your Redemptions</p>
                <p className="font-medium text-lg">{summary?.your_redemptions || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Available to You</p>
                <p className="font-medium text-lg">{deals.filter(d => d.can_redeem).length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search deals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Deal Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {dealTypes.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPartner} onValueChange={setFilterPartner}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Partner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Partners</SelectItem>
              {partnerships.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.partner_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Deals Grid */}
        {filteredDeals.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Ticket className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-heading text-xl mb-2">No deals available</h3>
              <p className="text-muted-foreground">
                {deals.length === 0 
                  ? "Check back soon for exclusive partner deals!"
                  : "Try adjusting your filters"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDeals.map((deal) => {
              const colorClass = dealColors[deal.deal_type] || 'bg-gray-100 text-gray-600';
              const discount = formatDiscount(deal);

              return (
                <Card 
                  key={deal.id} 
                  className={`card-interactive bg-gray-50 border-gray-200 ${!deal.can_redeem ? 'opacity-60' : ''}`}
                  data-testid={`deal-${deal.id}`}
                >
                  <CardContent className="p-4">
                    {/* Beta Test Badge */}
                    <div className="mb-2">
                      <Badge variant="outline" className="text-xs bg-gray-200 text-gray-600 border-gray-300">
                        Test Data - Not Valid IRL
                      </Badge>
                    </div>
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {deal.partner_logo ? (
                          <img 
                            src={deal.partner_logo} 
                            alt={deal.partner_name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClass}`}>
                            <DealIcon type={deal.deal_type} />
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-muted-foreground">{deal.partner_name}</p>
                          <h3 className="font-heading font-medium">{deal.title}</h3>
                        </div>
                      </div>
                      {discount && (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          {discount}
                        </Badge>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {deal.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {deal.is_online && (
                        <Badge variant="outline" className="text-xs">
                          Online
                        </Badge>
                      )}
                      {deal.is_in_store && (
                        <Badge variant="outline" className="text-xs">
                          In-Store
                        </Badge>
                      )}
                      {deal.end_date && (
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Expires {new Date(deal.end_date).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 btn-primary" 
                        disabled={!deal.can_redeem}
                        onClick={() => {
                          setSelectedDeal(deal);
                          setDetailDialogOpen(true);
                        }}
                      >
                        {deal.can_redeem ? 'View & Redeem' : 'Already Redeemed'}
                      </Button>
                    </div>

                    {/* Redemption status */}
                    {deal.user_redemptions > 0 && (
                      <p className="text-xs text-center text-muted-foreground mt-2">
                        You've redeemed this {deal.user_redemptions} time(s)
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Deal Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          {selectedDeal && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  {selectedDeal.partner_logo ? (
                    <img 
                      src={selectedDeal.partner_logo} 
                      alt={selectedDeal.partner_name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  ) : (
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${dealColors[selectedDeal.deal_type]}`}>
                      <DealIcon type={selectedDeal.deal_type} />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-normal text-muted-foreground">{selectedDeal.partner_name}</p>
                    <p>{selectedDeal.title}</p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {formatDiscount(selectedDeal) && (
                  <div className="text-center py-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">
                      {formatDiscount(selectedDeal)}
                    </p>
                  </div>
                )}

                <p className="text-muted-foreground">{selectedDeal.description}</p>

                {selectedDeal.promo_code && selectedDeal.can_redeem && (
                  <div className="bg-muted/50 p-4 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground mb-1">Promo Code</p>
                    <p className="text-xl font-mono font-bold">{selectedDeal.promo_code}</p>
                  </div>
                )}

                {selectedDeal.min_purchase && (
                  <p className="text-sm text-muted-foreground">
                    Minimum purchase: ${selectedDeal.min_purchase}
                  </p>
                )}

                {selectedDeal.terms_conditions && (
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium mb-1">Terms & Conditions:</p>
                    <p>{selectedDeal.terms_conditions}</p>
                  </div>
                )}

                {selectedDeal.redemption_instructions && (
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="font-medium text-sm mb-1">How to Redeem:</p>
                    <p className="text-sm text-muted-foreground">{selectedDeal.redemption_instructions}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  {selectedDeal.redemption_url && (
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => window.open(selectedDeal.redemption_url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Visit Website
                    </Button>
                  )}
                  <Button 
                    className="flex-1 btn-primary"
                    disabled={!selectedDeal.can_redeem || redeeming}
                    onClick={() => handleRedeem(selectedDeal)}
                  >
                    {redeeming && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {selectedDeal.can_redeem ? 'Redeem Deal' : 'Already Redeemed'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerDeals;
